import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { InsightTooltip } from "./InsightTooltip";
import { MultiInsightTooltip } from "./MultiInsightTooltip";

interface CustomMarkdownRendererProps {
  content: string;
  showDocumentLink?: boolean;
}

export const CustomMarkdownRenderer: React.FC<CustomMarkdownRendererProps> = ({
  content,
  showDocumentLink = true, // Default to true to maintain backward compatibility
}) => {
  const renderWithInsights = (node: React.ReactNode): React.ReactNode => {
    if (Array.isArray(node)) {
      return node.map((child, i) => (
        <React.Fragment key={i}>{renderWithInsights(child)}</React.Fragment>
      ));
    }

    if (typeof node !== "string") {
      return node;
    }

    const text = String(node);
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const pattern = /\[INS:([a-zA-Z0-9,\s:INS]+)\]/g;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      const matchIndex = match.index;
      if (matchIndex > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Split by INS: and filter out empty strings
      const insightIds = match[1].split("INS:").filter(Boolean);
      const cleanInsightIds = insightIds.map((id) => id.replace(/[,\s]/g, ""));

      if (cleanInsightIds.length === 1) {
        // Single insight
        parts.push(
          <InsightTooltip
            key={`insight-${cleanInsightIds[0]}-${matchIndex}`}
            insightId={cleanInsightIds[0]}
            showDocumentLink={showDocumentLink}
          />
        );
      } else {
        // Multiple insights
        parts.push(
          <MultiInsightTooltip
            key={`multi-insight-${matchIndex}`}
            insightIds={cleanInsightIds}
            showDocumentLink={showDocumentLink}
          />
        );
      }
      lastIndex = pattern.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p>{renderWithInsights(children)}</p>,
        span: ({ children }) => <span>{renderWithInsights(children)}</span>,
        li: ({ children }) => <li>{renderWithInsights(children)}</li>,
        h1: ({ children }) => <h1>{renderWithInsights(children)}</h1>,
        h2: ({ children }) => <h2>{renderWithInsights(children)}</h2>,
        h3: ({ children }) => <h3>{renderWithInsights(children)}</h3>,
        h4: ({ children }) => <h4>{renderWithInsights(children)}</h4>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
